import React from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import AgentAvatar from "../AgentAvatar";
import MarkdownContent from "../MarkdownContent";
import { AVATAR_SIZE, AVATAR_RADIUS } from "./conversationConstants";
import {
  messageRowSx,
  formatDateLabel,
  getAgentAvatarClickHandler,
  getClickableAgentAvatarSx,
} from "./conversationUtils";

export function DateDivider({ dateStr }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", px: 2.5, my: 1.5 }}>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
      <Typography
        sx={{
          px: 1.5,
          fontSize: "12px",
          fontWeight: 600,
          color: "text.secondary",
          whiteSpace: "nowrap",
          lineHeight: 1,
        }}
      >
        {formatDateLabel(dateStr)}
      </Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
    </Box>
  );
}

export function deriveToolLabelFromParts(typedMessages) {
  if (!Array.isArray(typedMessages) || typedMessages.length === 0) return null;
  const lastMsg = typedMessages[typedMessages.length - 1];
  if (!lastMsg || lastMsg.status === "done") return null;
  const activeParts = (lastMsg.parts || []).filter(
    (p) => p.type === "tool" && (p.state === "input-streaming" || p.state === "input-available")
  );
  if (activeParts.length === 0) return null;
  const toolName = activeParts[activeParts.length - 1].toolName;
  return toolName === "unknown_tool" ? null : toolName;
}

export function ThinkingIndicator({ agentName, agentId, onOpenAgentSettings, label, messages }) {
  const derivedLabel = label || (messages ? deriveToolLabelFromParts(messages) : null);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  return (
    <Box sx={{ ...messageRowSx, pt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <AgentAvatar name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        {derivedLabel ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              pt: "8px",
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                animation: "toolColorPulse 3s infinite ease-in-out",
                flexShrink: 0,
                "@keyframes toolColorPulse": {
                  "0%": { backgroundColor: "#3b82f6", transform: "scale(0.85)" },
                  "25%": { backgroundColor: "#8b5cf6", transform: "scale(1.1)" },
                  "50%": { backgroundColor: "#ec4899", transform: "scale(0.85)" },
                  "75%": { backgroundColor: "#f59e0b", transform: "scale(1.1)" },
                  "100%": { backgroundColor: "#3b82f6", transform: "scale(0.85)" },
                },
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {derivedLabel}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{ display: "flex", gap: 0.5, alignItems: "center", pt: "7px" }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: "text.disabled",
                  animation: "agentPulse 1.4s infinite ease-in-out",
                  animationDelay: `${i * 0.2}s`,
                  "@keyframes agentPulse": {
                    "0%, 80%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
                    "40%": { opacity: 1, transform: "scale(1)" },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function ThinkingAgentsPanel({
  thinkingAgents,
  streamingDeltas,
  onOpenAgentSettings,
}) {
  if (!Array.isArray(thinkingAgents) || thinkingAgents.length === 0) return null;
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {thinkingAgents.map((agent) => {
        const streamedContent = streamingDeltas?.[agent.agentId] || "";
        const agentName = agent.agentName || "Agent";
        const avatarClick = getAgentAvatarClickHandler(
          agent.agentId,
          onOpenAgentSettings
        );
        return (
          <Box
            key={agent.agentId || agent.agentName}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
            }}
          >
            <AgentAvatar name={agentName} size={AVATAR_SIZE} onClick={avatarClick} sx={{ mt: "2px" }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                {agentName}
              </Typography>
              {streamedContent ? (
                <MarkdownContent
                  sx={{
                    fontSize: 14,
                    color: "text.primary",
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                    mt: 0.25,
                  }}
                >
                  {streamedContent}
                </MarkdownContent>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.75,
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <CircularProgress
                    size={12}
                    thickness={6}
                    sx={{ color: "text.secondary" }}
                  />
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    Generating response...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export function ExecutionFallbackIndicator({ label = "Processing" }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 0.5,
        display: "flex",
        alignItems: "center",
        gap: 0.9,
      }}
    >
      <CircularProgress
        size={12}
        thickness={6}
        sx={{ color: "text.secondary", flexShrink: 0 }}
      />
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}...</Typography>
    </Box>
  );
}

export function ConnectionStatusBanner({ connectionState, streamError }) {
  if (connectionState === "reconnecting") {
    return (
      <Box
        sx={{
          mx: 2.5,
          mb: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: "8px",
          backgroundColor: "warning.main",
          color: "warning.contrastText",
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: 13,
        }}
      >
        <CircularProgress size={12} thickness={6} sx={{ color: "inherit", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "inherit", fontWeight: 500 }}>Reconnecting...</Typography>
      </Box>
    );
  }
  if (connectionState === "disconnected" || streamError) {
    return (
      <Box
        sx={{
          mx: 2.5,
          mb: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: "8px",
          backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(244,67,54,0.15)" : "rgba(244,67,54,0.08)",
          color: "error.main",
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: 13,
        }}
      >
        <WifiOffIcon sx={{ fontSize: 16, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "inherit", fontWeight: 500 }}>
          {streamError || "Connection lost. Refresh to reconnect."}
        </Typography>
      </Box>
    );
  }
  return null;
}

export function SendErrorBanner({ error, onDismiss }) {
  if (!error) return null;
  return (
    <Box
      sx={{
        mx: 2.5,
        mb: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(244,67,54,0.15)" : "rgba(244,67,54,0.08)",
        color: "error.main",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "inherit", fontWeight: 500, flex: 1 }}>
        {error}
      </Typography>
      {onDismiss && (
        <IconButton size="small" onClick={onDismiss} sx={{ color: "inherit", p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
}

export function SessionErrorBanner({ status, statusReason }) {
  if (status !== "ERROR") return null;
  return (
    <Box
      sx={{
        mx: 2.5,
        mb: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(244,67,54,0.15)" : "rgba(244,67,54,0.08)",
        color: "error.main",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "inherit", fontWeight: 500 }}>
        {statusReason || "Session encountered an error."}
      </Typography>
    </Box>
  );
}

export function WaitingForWorkersBanner({ status, workerCount }) {
  if (status !== "WAITING_FOR_WORKERS") return null;
  const label = workerCount
    ? `Waiting for workers \u2014 ${workerCount} blocking worker${workerCount === 1 ? "" : "s"} in progress`
    : "Waiting for workers to complete\u2026";
  return (
    <Box
      sx={{
        mx: 2.5,
        mb: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(139, 92, 246, 0.12)"
            : "rgba(139, 92, 246, 0.08)",
        color: "rgb(139, 92, 246)",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
      }}
    >
      <PauseCircleOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "inherit", fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}
